/**
 * Tipos e rótulos do cadastro do posto da AET em etapas (issue #356, D11).
 */
import type {
  ErgonomicOptions,
  ErgonomicRiskLevel,
  ErgonomicWorkPace,
  ErgonomicWorkstationDetail,
} from '../../../../types/ergonomicWorkstation';

export type WizardStepKey = 'situation' | 'organization' | 'furniture' | 'conclusion' | 'photos';

export const ERGONOMIC_WIZARD_STEPS: ReadonlyArray<{ key: WizardStepKey; label: string }> = [
  { key: 'situation', label: 'Situação do trabalho' },
  { key: 'organization', label: 'Organização e levantamento' },
  { key: 'furniture', label: 'Mobiliário e equipamentos' },
  { key: 'conclusion', label: 'Conclusão' },
  { key: 'photos', label: 'Fotos' },
];

/**
 * Os campos do posto como a tela os edita: tudo texto (campo numérico vazio é '' e vira null no envio).
 * As etapas 1 a 3 mexem nos campos delas; `riskLevel`, `riskDescription` e `actionGuidance` são da etapa 4.
 */
export interface WorkstationDraft {
  /** Só na criação, quando o cabeçalho não tem empresa ativa. */
  companyId: string;
  departmentId: string;
  name: string;
  process: string;
  exposedCount: string;
  workLocation: string;
  workPace: ErgonomicWorkPace | '';
  cycleMinutes: string;
  productionNorm: string;
  operatingMode: string;
  timeRequirement: string;
  taskContent: string;
  cognitiveAspect: string;
  movementRequired: string;
  effortRequired: string;
  workSurface: string;
  reachZone: string;
  identifiedPosture: string;
  circulationArea: string;
  existingControls: string;
  riskLevel: ErgonomicRiskLevel | '';
  riskDescription: string;
  actionGuidance: string;
}

export type WorkstationErrors = Partial<Record<keyof WorkstationDraft, string>>;

export const EMPTY_DRAFT: WorkstationDraft = {
  companyId: '',
  departmentId: '',
  name: '',
  process: '',
  exposedCount: '',
  workLocation: '',
  workPace: '',
  cycleMinutes: '',
  productionNorm: '',
  operatingMode: '',
  timeRequirement: '',
  taskContent: '',
  cognitiveAspect: '',
  movementRequired: '',
  effortRequired: '',
  workSurface: '',
  reachZone: '',
  identifiedPosture: '',
  circulationArea: '',
  existingControls: '',
  riskLevel: '',
  riskDescription: '',
  actionGuidance: '',
};

/**
 * O que toda etapa recebe do Wizard. Contrato de toda etapa do cadastro:
 * - `workstation`: o posto já salvo (só a etapa 1 roda sem ele); o id vem daí.
 * - `options`: listas fechadas de `GET /options` (`riskLevels`, `photoLimits`...); null enquanto não chegaram.
 * - `values`/`errors`/`onChange`: o rascunho dos campos do posto. A etapa 4 edita `riskLevel`,
 *   `riskDescription` e `actionGuidance` por aqui e o "Salvar e continuar" do Wizard faz o PUT.
 * - `canEdit`: tem `ergonomic_workstation.update`; sem ele a etapa só mostra.
 * - `disabled`: o Wizard está gravando; trave os campos.
 * - `onSaved`: etapa que grava item na hora (dano, ação, foto) avisa aqui e o Wizard mostra a confirmação.
 */
export interface WizardStepProps {
  workstation: ErgonomicWorkstationDetail;
  options: ErgonomicOptions | null;
  values: WorkstationDraft;
  errors: WorkstationErrors;
  canEdit: boolean;
  disabled: boolean;
  onChange: (patch: Partial<WorkstationDraft>) => void;
  onSaved: (message: string) => void;
}
